import React from 'react';
import { TagsInput } from '@ark-ui/react/tags-input';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface TagsInputFieldProps extends BaseInputProps<string[]> {
  /** Maximální počet tagů. */
  max?: number;
  className?: string;
}

/**
 * Atomický input pro zadávání tagů postavený na Ark UI `TagsInput`.
 *
 * RHF drží hodnotu jako pole `string[]`. `id` z `<FormField />` se mapuje přes
 * `ids.input` — přímé přepsání `id` na `<TagsInput.Input>` by rozbilo zag
 * lookupy (mazání rozepsaného textu po přidání tagu, správa fokusu). RHF
 * `ref` míří na viditelný input, aby fokus při chybě (`shouldFocusError`)
 * skončil v editovatelném prvku.
 */
export const TagsInputField: React.FC<TagsInputFieldProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  placeholder,
  describedBy,
  testAttributes,
  max,
  className,
}) => (
  <TagsInput.Root
    name={name}
    ids={{ input: id }}
    value={value ?? []}
    disabled={disabled}
    readOnly={readOnly}
    invalid={hasError}
    max={max}
    onValueChange={(details) => onChange(details.value)}
    onBlur={onBlur}
    className={clsx(className)}
  >
    <TagsInput.Context>
      {(tagsInput) => (
        <TagsInput.Control className={styles.tagsControl}>
          {tagsInput.value.map((tag, index) => (
            <TagsInput.Item key={`${tag}-${index}`} index={index} value={tag}>
              <TagsInput.ItemPreview className={styles.tagItemPreview}>
                <TagsInput.ItemText>{tag}</TagsInput.ItemText>
                <TagsInput.ItemDeleteTrigger className={styles.tagDeleteTrigger}>
                  <Icon name="x-mark" size={14} />
                </TagsInput.ItemDeleteTrigger>
              </TagsInput.ItemPreview>
              <TagsInput.ItemInput className={styles.tagItemInput} />
            </TagsInput.Item>
          ))}
          <TagsInput.Input
            ref={ref}
            placeholder={placeholder}
            className={styles.tagsInput}
            aria-describedby={describedBy}
          />
        </TagsInput.Control>
      )}
    </TagsInput.Context>
    <TagsInput.HiddenInput {...testAttributes} />
  </TagsInput.Root>
);

TagsInputField.displayName = 'TagsInputField';
